import { Button, ButtonRow, FormGroup, FormParagraph, Heading, Text, TextInput } from '@ksyos/design-system';
import { Link as ReactRouterLink, useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { GroupAddEditValidated } from '@common/validation/group';
import type { IKisSettingAddEditValidated } from '@common/validation/kisSetting';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { groupAddEditSchema } from '@common/validation/group';
import { GroupSectionHeading } from '@frontend/components/elements/GroupSectionHeading';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { IdentifierDeclaration } from '@frontend/components/userDataManagement/IdentifierDeclaration';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    getValidationErrorTranslation,
    hasOneAuthorization,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { FormContainer, Loading } from '../../../elements/common';
import { ExtraGroupSettingsEdit } from './ExtraGroupSettingsEdit';
import { ExtraGroupSettingsRead } from './ExtraGroupSettingsRead';

export interface IGroup {
    bankAccountDetails?: {
        bankAccountNumber: string;
        id: string;
        subscriber: string;
    };
    financialEmailAddress?: string | null;
    id?: string;
    identifierDeclaration?: string | null;
    isActive: boolean;
    kisSetting?: IKisSettingAddEditValidated;
    name: string;
    relationNumber?: string | null;
}

export function GroupsAddEdit() {
    const { id: groupId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const returnPage = searchParams.get('returnPage');
    const [isSaving, setIsSaving] = useState(false);
    const [group, setGroup] = useState<IGroup>();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const translate = getSelectedLanguage();

    assertHasAuthorization([
        ManagerAuthorization.groupAddEdit,
        ManagerAuthorization.groupEditFinanceData,
        ManagerAuthorization.groupRead,
    ]);

    useEffect(() => {
        if (groupId) {
            load();
        } else {
            setGroup({
                identifierDeclaration: null,
                isActive: true,
                name: '',
            });
        }
    }, []);

    return (
        <PageTemplate>
            <Heading mb={[4, 6]}>{getHeaderText()}</Heading>

            <Loading
                item={group}
                loaded={(groupToLoad) => (
                    <FormContainer onSubmit={async () => await handleSubmit()}>
                        <GroupSectionHeading sectionName={translate.groups.details} />

                        {hasOneAuthorization(ManagerAuthorization.groupAddEdit) && (
                            <>
                                <FormGroup
                                    hasErrors={errors.has('name')}
                                    isRequired={true}
                                    label={translate.groups.groupName}
                                >
                                    <TextInput
                                        error={getValidationErrorTranslation(errors.get('name'))}
                                        onBlur={async () => await validateOne('name')}
                                        onChange={async (e) => await handleChange('name', e.target.value)}
                                        placeholder={translate.groups.groupName}
                                        value={groupToLoad.name}
                                    />
                                </FormGroup>
                                <IdentifierDeclaration
                                    errors={errors}
                                    onChange={async (name, value) =>
                                        await handleChange('identifierDeclaration', value as string | null)
                                    }
                                    onValidateOne={async (name) => await validateOne(name)}
                                    value={groupToLoad}
                                />
                            </>
                        )}

                        {!hasOneAuthorization(ManagerAuthorization.groupAddEdit) && (
                            <FormGroup label={translate.groups.groupName}>
                                <Text>{groupToLoad.name || '-'}</Text>
                            </FormGroup>
                        )}

                        {!hasOneAuthorization(ManagerAuthorization.groupEditFinanceData) && (
                            <>
                                <FormGroup label={translate.organization.relationNumber}>
                                    <Text>{groupToLoad.relationNumber || '-'}</Text>
                                </FormGroup>
                                <FormGroup label={translate.identifierDeclarationOrRegistration.identifierDeclaration}>
                                    <Text>{groupToLoad.identifierDeclaration ?? '-'}</Text>
                                </FormGroup>
                            </>
                        )}

                        {hasOneAuthorization(ManagerAuthorization.groupEditFinanceData) && (
                            <>
                                <FormGroup
                                    hasErrors={errors.has('relationNumber')}
                                    label={translate.organization.relationNumber}
                                >
                                    <TextInput
                                        error={getValidationErrorTranslation(errors.get('relationNumber'))}
                                        onBlur={async () => await validateOne('relationNumber')}
                                        onChange={async (e) =>
                                            await handleChange('relationNumber', e.target.value ? e.target.value : null)
                                        }
                                        placeholder={translate.organization.relationNumber}
                                        value={groupToLoad.relationNumber ?? ''}
                                    />
                                </FormGroup>

                                <FormGroup
                                    hasErrors={errors.has('financialEmailAddress')}
                                    label={translate.groups.financialEmailAddress}
                                    tooltipText={translate.groups.financialEmailAddressTooltip}
                                >
                                    <TextInput
                                        error={getValidationErrorTranslation(errors.get('financialEmailAddress'))}
                                        onBlur={async () => await validateOne('financialEmailAddress')}
                                        onChange={async (e) =>
                                            await handleChange(
                                                'financialEmailAddress',
                                                e.target.value ? e.target.value : null
                                            )
                                        }
                                        placeholder={translate.groups.financialEmailAddress}
                                        value={groupToLoad.financialEmailAddress ?? ''}
                                    />
                                </FormGroup>
                            </>
                        )}

                        {groupToLoad.id && hasOneAuthorization(ManagerAuthorization.groupReadFinanceData) && (
                            <>
                                <FormGroup label={translate.groups.bankAccountNumber}>
                                    <FormParagraph>
                                        {groupToLoad.bankAccountDetails?.bankAccountNumber ?? '-'}
                                    </FormParagraph>
                                </FormGroup>

                                <FormGroup label={translate.groups.subscriber}>
                                    <FormParagraph>{groupToLoad.bankAccountDetails?.subscriber ?? '-'}</FormParagraph>
                                </FormGroup>
                            </>
                        )}

                        {hasOneAuthorization(ManagerAuthorization.groupAddEdit) ? (
                            <ExtraGroupSettingsEdit
                                errors={errors}
                                group={groupToLoad}
                                handleChange={handleChange}
                                handleKisSettingChange={handleKisSettingChange}
                                validateOne={validateOne}
                            />
                        ) : (
                            <ExtraGroupSettingsRead group={groupToLoad} />
                        )}

                        {hasOneAuthorization([
                            ManagerAuthorization.groupAddEdit,
                            ManagerAuthorization.groupEditFinanceData,
                        ]) ? (
                            <ButtonRow>
                                <Button
                                    as={ReactRouterLink}
                                    isDisabled={isSaving}
                                    to="/groups"
                                    variant="outline"
                                >
                                    {translate.common.cancel}
                                </Button>
                                <Button
                                    iconRight="caret-right"
                                    isDisabled={isSaving}
                                    type="submit"
                                >
                                    {translate.common.save}
                                </Button>
                            </ButtonRow>
                        ) : (
                            <Button
                                as={ReactRouterLink}
                                to={`/groups/${groupId}`}
                                variant="outline"
                            >
                                {translate.common.goBack}
                            </Button>
                        )}
                    </FormContainer>
                )}
            />
        </PageTemplate>
    );

    async function handleChange<K extends keyof IGroup>(name: K, value: IGroup[K]) {
        setGroup({ ...group, [name]: value } as IGroup);
    }

    async function handleKisSettingChange<K extends keyof IKisSettingAddEditValidated>(
        name: K,
        value: IKisSettingAddEditValidated[K]
    ) {
        if (!group?.kisSetting) {
            return;
        }
        await handleChange('kisSetting', { ...group.kisSetting, [name]: value });
    }

    async function validateAndSetErrors() {
        const { errors: validatedErrors, item } = validate(groupAddEditSchema, group);
        setErrors(validatedErrors);

        return { item, validatedErrors };
    }

    async function validateOne(name: string) {
        setErrors(updateValidationErrorsForOne(groupAddEditSchema, group, errors, name));
    }

    async function load() {
        const url = `/api/groups/${groupId}`;
        const result = await fetchJson(url, 'GET');
        setGroup(result.json);
    }

    function redirect() {
        let returnUrl = '/groups';
        if (returnPage) {
            returnUrl += `?page=${returnPage}`;
        }
        navigate(returnUrl);
    }

    async function handleSubmit() {
        const { validatedErrors, item } = await validateAndSetErrors();

        if (validatedErrors.size > 0) {
            return;
        }

        const isSavedSuccessfully = await save(item);

        if (isSavedSuccessfully) {
            redirect();
        }
    }

    async function save(item: GroupAddEditValidated | undefined) {
        if (!item) {
            return false;
        }

        setIsSaving(true);

        let url = '/api/groups/';
        if (groupId) {
            url += groupId;
        }
        if (!hasOneAuthorization(ManagerAuthorization.groupEditFinanceData)) {
            const { relationNumber, financialEmailAddress, ...itemWithoutFinancialData } = item;
            item = itemWithoutFinancialData;
        }

        const result = await fetchJson(url, 'POST', item, [
            ValidationErrorCode.RELATION_NUMBER_EXISTS,
            ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS,
        ]);
        setIsSaving(false);

        if (result?.errorCode === ValidationErrorCode.RELATION_NUMBER_EXISTS) {
            setErrors(emptyValidationErrors.set('relationNumber', ValidationErrorCode.RELATION_NUMBER_EXISTS));

            return false;
        } else if (result?.errorCode === ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS) {
            setErrors(
                emptyValidationErrors.set('identifierDeclaration', ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS)
            );

            return false;
        }

        return true;
    }

    function getHeaderText() {
        if (hasOneAuthorization([ManagerAuthorization.groupEditFinanceData, ManagerAuthorization.groupAddEdit])) {
            return `${translate.groups.group} ${groupId ? translate.common.edit : translate.common.new}`;
        }

        return formatString(translate.common.detailsItem, [translate.groups.group]);
    }
}
